import { useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { Link } from 'react-router-dom';
import type { BonusType, BonusZone, TodayBonusRow } from '../../api/bonuses';
import {
    buildBonusColumns,
    findExistingZoneAssignment,
    groupRowsByBonus,
    isUnconfirmedAssignment,
    loadColumnOrder,
    saveColumnOrder,
    type BonusColumnDefinition
} from './resourceHunterUtils';
import { ReplacementConfirmPopover } from './ReplacementConfirmPopover';
import { ZoneMetadataLine } from './ZoneMetadataLine';

type DailyBonusesGridProps = {
    rows: TodayBonusRow[];
    assignmentRows?: TodayBonusRow[];
    zones: BonusZone[];
    bonusTypes: BonusType[];
    canEdit: boolean;
    readOnly?: boolean;
    isSubmitting?: boolean;
    isRemoving?: boolean;
    onSubmit?: (zoneId: string, bonusTypeId: string) => void;
    onRemove?: (bonusId: string) => void;
};

type DragState = {
    key: string;
    insertionIndex: number;
    markerLeft: number;
    markerTop: number;
    markerHeight: number;
};

function reorderToInsertion(keys: string[], activeKey: string, insertionIndex: number) {
    const withoutActive = keys.filter((key) => key !== activeKey);
    const boundedIndex = Math.max(0, Math.min(insertionIndex, withoutActive.length));
    const next = [...withoutActive];
    next.splice(boundedIndex, 0, activeKey);
    return next;
}

function getDropPlacement(container: HTMLElement, activeKey: string, clientX: number, clientY: number) {
    const containerRect = container.getBoundingClientRect();
    const columns = Array.from(container.querySelectorAll<HTMLElement>('[data-bonus-column-key]')).filter(
        (column) => column.dataset.bonusColumnKey && column.dataset.bonusColumnKey !== activeKey
    );

    if (columns.length === 0) {
        return { insertionIndex: 0, markerLeft: 0, markerTop: 0, markerHeight: containerRect.height };
    }

    const columnRects = columns.map((column, index) => {
        const rect = column.getBoundingClientRect();
        return {
            index,
            rect,
            centerX: rect.left + rect.width / 2,
            centerY: rect.top + rect.height / 2
        };
    });

    const rowTolerance = 8;
    const rows: Array<typeof columnRects> = [];

    for (const item of columnRects) {
        const existingRow = rows.find((row) => Math.abs(row[0].rect.top - item.rect.top) <= rowTolerance);
        if (existingRow) {
            existingRow.push(item);
        } else {
            rows.push([item]);
        }
    }

    const sortedRows = rows
        .map((row) => row.sort((a, b) => a.rect.left - b.rect.left))
        .sort((a, b) => a[0].rect.top - b[0].rect.top);

    let targetRow = sortedRows[0];
    let bestRowDistance = Number.POSITIVE_INFINITY;

    for (const row of sortedRows) {
        const rowTop = Math.min(...row.map((item) => item.rect.top));
        const rowBottom = Math.max(...row.map((item) => item.rect.bottom));
        const rowDistance = clientY >= rowTop && clientY <= rowBottom ? 0 : Math.min(Math.abs(clientY - rowTop), Math.abs(clientY - rowBottom));

        if (rowDistance < bestRowDistance) {
            bestRowDistance = rowDistance;
            targetRow = row;
        }
    }

    let closest = targetRow[0];
    let closestHorizontalDistance = Number.POSITIVE_INFINITY;

    for (const item of targetRow) {
        const distance = Math.abs(item.centerX - clientX);
        if (distance < closestHorizontalDistance) {
            closestHorizontalDistance = distance;
            closest = item;
        }
    }

    const insertBefore = clientX < closest.centerX;
    const insertionIndex = insertBefore ? closest.index : closest.index + 1;
    const closestRowIndex = targetRow.findIndex((item) => item.index === closest.index);
    const previousColumn = insertBefore ? targetRow[closestRowIndex - 1] : closest;
    const nextColumn = insertBefore ? closest : targetRow[closestRowIndex + 1];

    let markerViewportLeft: number;
    if (previousColumn && nextColumn) {
        markerViewportLeft = (previousColumn.rect.right + nextColumn.rect.left) / 2;
    } else if (nextColumn) {
        markerViewportLeft = nextColumn.rect.left;
    } else if (previousColumn) {
        markerViewportLeft = previousColumn.rect.right;
    } else {
        markerViewportLeft = closest.rect.left;
    }

    const markerViewportTop = Math.min(...targetRow.map((item) => item.rect.top));
    const markerViewportBottom = Math.max(...targetRow.map((item) => item.rect.bottom));

    return {
        insertionIndex,
        markerLeft: markerViewportLeft - containerRect.left,
        markerTop: markerViewportTop - containerRect.top,
        markerHeight: markerViewportBottom - markerViewportTop
    };
}

export function DailyBonusesGrid({
    rows,
    assignmentRows,
    zones,
    bonusTypes,
    canEdit,
    readOnly = false,
    isSubmitting = false,
    isRemoving = false,
    onSubmit,
    onRemove
}: DailyBonusesGridProps) {
    const gridRef = useRef<HTMLDivElement | null>(null);
    const dragImageRef = useRef<HTMLElement | null>(null);
    const [columnOrder, setColumnOrder] = useState(() => loadColumnOrder(bonusTypes, rows));
    const [dragState, setDragState] = useState<DragState | null>(null);

    useEffect(() => {
        setColumnOrder((current) => {
            const loaded = loadColumnOrder(bonusTypes, rows);
            const merged = [...current, ...loaded.filter((key) => !current.includes(key))];
            saveColumnOrder(merged);
            return merged;
        });
    }, [bonusTypes, rows]);

    const groupedRows = useMemo(() => groupRowsByBonus(rows), [rows]);
    const allAssignmentRows = assignmentRows ?? rows;
    const columns = useMemo(() => buildBonusColumns(bonusTypes, rows, columnOrder), [bonusTypes, columnOrder, rows]);

    function clearDragImage() {
        dragImageRef.current?.remove();
        dragImageRef.current = null;
    }

    function handleColumnDragStart(event: DragEvent<HTMLElement>, columnKey: string) {
        if (readOnly) {
            event.preventDefault();
            return;
        }

        const target = event.target as HTMLElement;
        if (target.closest('button, a, input, select, textarea')) {
            event.preventDefault();
            return;
        }

        const columnElement = event.currentTarget.closest<HTMLElement>('[data-bonus-column-key]');
        if (!columnElement) {
            event.preventDefault();
            return;
        }

        const rect = columnElement.getBoundingClientRect();
        const clone = columnElement.cloneNode(true) as HTMLElement;
        clone.classList.add('resource-bonus-drag-image');
        clone.style.width = `${rect.width}px`;
        clone.style.position = 'fixed';
        clone.style.top = '-10000px';
        clone.style.left = '-10000px';
        clone.style.pointerEvents = 'none';
        document.body.appendChild(clone);
        dragImageRef.current = clone;

        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', columnKey);
        event.dataTransfer.setDragImage(clone, event.clientX - rect.left, event.clientY - rect.top);

        setDragState({
            key: columnKey,
            insertionIndex: columns.findIndex((column) => column.key === columnKey),
            markerLeft: rect.left - (gridRef.current?.getBoundingClientRect().left ?? 0),
            markerTop: rect.top - (gridRef.current?.getBoundingClientRect().top ?? 0),
            markerHeight: rect.height
        });
    }

    function handleGridDragOver(event: DragEvent<HTMLDivElement>) {
        if (!dragState || !gridRef.current) {
            return;
        }

        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';

        const placement = getDropPlacement(gridRef.current, dragState.key, event.clientX, event.clientY);
        setDragState((current) => {
            if (
                !current ||
                (current.insertionIndex === placement.insertionIndex &&
                    current.markerLeft === placement.markerLeft &&
                    current.markerTop === placement.markerTop &&
                    current.markerHeight === placement.markerHeight)
            ) {
                return current;
            }

            return { ...current, ...placement };
        });
    }

    function finishDrag(shouldReorder: boolean) {
        if (shouldReorder && dragState) {
            const visibleKeys = columns.map((column) => column.key);
            const nextVisibleOrder = reorderToInsertion(visibleKeys, dragState.key, dragState.insertionIndex);
            const next = [...nextVisibleOrder, ...columnOrder.filter((key) => !visibleKeys.includes(key))];
            setColumnOrder(next);
            saveColumnOrder(next);
        }

        setDragState(null);
        clearDragImage();
    }

    function handleGridDrop(event: DragEvent<HTMLDivElement>) {
        if (!dragState) {
            return;
        }

        event.preventDefault();
        finishDrag(true);
    }

    function handleColumnDragEnd() {
        finishDrag(false);
    }

    return (
        <div
            className={`resource-bonus-grid${dragState ? ' is-reordering' : ''}`}
            aria-label="Daily zone bonuses"
            ref={gridRef}
            onDragOver={handleGridDragOver}
            onDrop={handleGridDrop}
        >
            {columns.map((column) => {
                const isDraggingSource = dragState?.key === column.key;
                return (
                    <BonusColumn
                        key={column.key}
                        column={column}
                        rows={groupedRows.get(column.key) ?? []}
                        zones={zones}
                        allRows={allAssignmentRows}
                        canEdit={canEdit && !readOnly}
                        isSubmitting={isSubmitting}
                        isRemoving={isRemoving}
                        isDraggingSource={Boolean(isDraggingSource)}
                        onSubmit={onSubmit}
                        onRemove={onRemove}
                        onColumnDragStart={handleColumnDragStart}
                        onColumnDragEnd={handleColumnDragEnd}
                    />
                );
            })}
            {dragState ? (
                <div
                    className="resource-bonus-drop-marker"
                    aria-hidden="true"
                    style={{ left: `${dragState.markerLeft}px`, top: `${dragState.markerTop}px`, height: `${dragState.markerHeight}px` }}
                />
            ) : null}
        </div>
    );
}

type BonusColumnProps = {
    column: BonusColumnDefinition;
    rows: TodayBonusRow[];
    zones: BonusZone[];
    allRows: TodayBonusRow[];
    canEdit: boolean;
    isSubmitting: boolean;
    isRemoving: boolean;
    isDraggingSource: boolean;
    onSubmit?: (zoneId: string, bonusTypeId: string) => void;
    onRemove?: (bonusId: string) => void;
    onColumnDragStart: (event: DragEvent<HTMLElement>, columnKey: string) => void;
    onColumnDragEnd: () => void;
};

function BonusColumn({
    column,
    rows,
    zones,
    allRows,
    canEdit,
    isSubmitting,
    isRemoving,
    isDraggingSource,
    onSubmit,
    onRemove,
    onColumnDragStart,
    onColumnDragEnd
}: BonusColumnProps) {
    const [isAdding, setIsAdding] = useState(false);

    return (
        <section
            className={`resource-bonus-column${column.key === 'none' || column.key === 'unconfirmed' ? ' is-wide' : ''}${isDraggingSource ? ' is-dragging-source' : ''}`}
            data-bonus-column-key={column.key}
        >
            <header
                className="resource-bonus-column__header"
                draggable={!isAdding}
                onDragStart={(event) => onColumnDragStart(event, column.key)}
                onDragEnd={onColumnDragEnd}
            >
                <ColumnHeaderContents column={column} rows={rows} canEdit={canEdit} onAddToggle={() => setIsAdding((value) => !value)} />
            </header>

            <BonusColumnContent
                column={column}
                rows={rows}
                zones={zones}
                allRows={allRows}
                canEdit={canEdit}
                isAdding={isAdding}
                setIsAdding={setIsAdding}
                isSubmitting={isSubmitting}
                isRemoving={isRemoving}
                onSubmit={onSubmit}
                onRemove={onRemove}
            />
        </section>
    );
}

type ColumnHeaderContentsProps = {
    column: BonusColumnDefinition;
    rows: TodayBonusRow[];
    canEdit: boolean;
    onAddToggle?: () => void;
};

function ColumnHeaderContents({ column, rows, canEdit, onAddToggle }: ColumnHeaderContentsProps) {
    return (
        <>
            <div>
                <h2>{column.label}</h2>
                <span>{rows.length} zone{rows.length === 1 ? '' : 's'}</span>
            </div>
            {canEdit && !column.isUnconfirmed && column.bonusTypeId ? (
                <button className="resource-bonus-column__add" type="button" aria-label={`Add ${column.label} bonus`} onClick={onAddToggle}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-plus" viewBox="0 0 16 16">
                        <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4" />
                    </svg>
                </button>
            ) : null}
        </>
    );
}

type BonusColumnContentProps = {
    column: BonusColumnDefinition;
    rows: TodayBonusRow[];
    zones: BonusZone[];
    allRows: TodayBonusRow[];
    canEdit: boolean;
    isAdding?: boolean;
    setIsAdding?: (value: boolean) => void;
    isSubmitting: boolean;
    isRemoving: boolean;
    onSubmit?: (zoneId: string, bonusTypeId: string) => void;
    onRemove?: (bonusId: string) => void;
};

function BonusColumnContent({
    column,
    rows,
    zones,
    allRows,
    canEdit,
    isAdding: controlledIsAdding,
    setIsAdding: controlledSetIsAdding,
    isSubmitting,
    isRemoving,
    onSubmit,
    onRemove
}: BonusColumnContentProps) {
    const [fallbackIsAdding, fallbackSetIsAdding] = useState(false);
    const [zoneSearch, setZoneSearch] = useState('');
    const [pendingReplacement, setPendingReplacement] = useState<null | {
        zoneId: string;
        zoneName: string;
        fromBonusLabel: string;
        toBonusTypeId: string;
        toBonusLabel: string;
    }>(null);
    const isAdding = controlledIsAdding ?? fallbackIsAdding;
    const setIsAdding = controlledSetIsAdding ?? fallbackSetIsAdding;

    const selectedZone = useMemo(() => {
        const normalized = zoneSearch.trim().toLowerCase();
        return zones.find((zone) => zone.name.toLowerCase() === normalized || zone.slug.toLowerCase() === normalized) ?? null;
    }, [zoneSearch, zones]);

    function handleSubmit() {
        if (!selectedZone || !column.bonusTypeId || !onSubmit) {
            return;
        }

        const existing = findExistingZoneAssignment(allRows, selectedZone.id);
        if (existing && !isUnconfirmedAssignment(existing) && existing.bonusTypeId !== column.bonusTypeId) {
            setPendingReplacement({
                zoneId: selectedZone.id,
                zoneName: selectedZone.name,
                fromBonusLabel: existing.bonusLabel,
                toBonusTypeId: column.bonusTypeId,
                toBonusLabel: column.label
            });
            return;
        }

        setPendingReplacement(null);
        onSubmit(selectedZone.id, column.bonusTypeId);
        setZoneSearch('');
        setIsAdding(false);
    }

    function handleConfirmReplacement() {
        if (!pendingReplacement || !onSubmit) {
            return;
        }

        onSubmit(pendingReplacement.zoneId, pendingReplacement.toBonusTypeId);
        setPendingReplacement(null);
        setZoneSearch('');
        setIsAdding(false);
    }

    return (
        <>
            {isAdding ? (
                <div className="resource-bonus-column__quick-add">
                    <input
                        className="form-control form-control-sm"
                        list={`bonus-zone-options-${column.key}`}
                        placeholder="Zone name..."
                        value={zoneSearch}
                        onChange={(event) => {
                            setZoneSearch(event.target.value);
                            setPendingReplacement(null);
                        }}
                    />
                    <datalist id={`bonus-zone-options-${column.key}`}>
                        {zones.map((zone) => (
                            <option key={zone.id} value={zone.name} />
                        ))}
                    </datalist>
                    <button className="btn btn-sm btn-primary" type="button" disabled={!selectedZone || isSubmitting} onClick={handleSubmit}>
                        Add
                    </button>
                    {pendingReplacement ? (
                        <ReplacementConfirmPopover
                            zoneName={pendingReplacement.zoneName}
                            fromBonusLabel={pendingReplacement.fromBonusLabel}
                            toBonusLabel={pendingReplacement.toBonusLabel}
                            isSubmitting={isSubmitting}
                            onCancel={() => setPendingReplacement(null)}
                            onConfirm={handleConfirmReplacement}
                        />
                    ) : null}
                </div>
            ) : null}

            <div className="resource-bonus-column__body">
                {rows.length === 0 ? <div className="resource-bonus-empty">No zones assigned.</div> : null}
                {rows.map((row) => (
                    <ZoneBonusRow
                        key={`${row.zoneId}-${row.id ?? row.state}`}
                        row={row}
                        canEdit={canEdit && !column.isUnconfirmed}
                        isRemoving={isRemoving}
                        onRemove={onRemove}
                    />
                ))}
            </div>
        </>
    );
}

type ZoneBonusRowProps = {
    row: TodayBonusRow;
    canEdit: boolean;
    isRemoving: boolean;
    onRemove?: (bonusId: string) => void;
};

function ZoneBonusRow({ row, canEdit, isRemoving, onRemove }: ZoneBonusRowProps) {
    return (
        <article className="resource-zone-row">
            <div className="resource-zone-row__info">
                <Link to={`/resource-hunter/zones/${row.zoneSlug}`} className="resource-zone-row__name">
                    {row.name}
                </Link>
                <div className="resource-zone-row__meta"><ZoneMetadataLine expansion={row.expansion} minLevel={row.minLevel} maxLevel={row.maxLevel} zoneType={row.zoneType} /></div>
            </div>

            {canEdit && row.id ? (
                <div className="resource-zone-row__actions">
                    <button
                        className="resource-zone-row__action resource-zone-row__action--flag"
                        type="button"
                        disabled={isRemoving}
                        aria-label={`Remove ${row.name}`}
                        title="Remove"
                        onClick={() => onRemove?.(row.id as string)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-x" viewBox="0 0 16 16">
                            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
                        </svg>
                    </button>
                </div>
            ) : null}
        </article>
    );
}
